import { useId, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDrag } from "./drag.tsx";
import "./action-guide.css";

type GuideKind = "decks" | "places" | "players";
type Hole = { x: number; y: number; width: number; height: number; rx: number; clipPath: string };

function playerSurfaces(panel: string, piece: string): string {
  return `${panel} .player-seal, ${panel} .player-card-name, ${panel} .player-vitals > span, ${panel} .seal-turn, ${piece}, ${piece} .piece-damage`;
}
const PLAYER_SURFACES = playerSurfaces(".player-panel.is-targetable", ".player-piece.is-targetable");

/** Preserve the real objects' appearance, including their own shapes; draw no focus decoration. */
export function ActionGuide({ kind, enabled, targetsKey }: {
  kind: GuideKind | null; enabled: boolean; targetsKey: string;
}) {
  const drag = useDrag();
  const mask = `guide-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const [holes, setHoles] = useState<Hole[]>([]);
  let activeKind = kind;
  let selector = kind === "decks" ? ".deck.is-draggable .card-back-art"
    : kind === "places" ? ".place-card.is-pickable"
      : kind === "players" ? PLAYER_SURFACES : "";
  if (drag) {
    const targets = [...drag.payload.actions.keys()];
    if (targets.length && targets.every((target) => target.startsWith("player:"))) {
      activeKind = "players";
      selector = targets.map((target) => {
        const id = CSS.escape(target.slice("player:".length));
        return playerSurfaces(`.player-panel[data-player-id="${id}"]`, `.player-piece[data-player-id="${id}"]`);
      }).join(", ");
    } else if (targets.length && targets.every((target) => target.startsWith("place:"))) {
      activeKind = "places";
      selector = targets.map((target) => `.place-card[data-drop="${CSS.escape(target)}"]`).join(", ");
    }
  }
  // Phase instructions and passing remain readable alongside the legal objects.
  if (selector) selector += ", [data-phase-control], .table-status > span, .table-status > small";

  useLayoutEffect(() => {
    if (!enabled || !selector) return;
    const targets = [...document.querySelectorAll<HTMLElement>(selector)];
    let frame = 0;
    let followUntil = 0;
    const measure = () => {
      const next: Hole[] = [];
      for (const target of targets) {
        let rect = target.getBoundingClientRect();
        if (target.parentElement?.classList.contains("table-status")) {
          const text = document.createRange();
          text.selectNodeContents(target);
          rect = text.getBoundingClientRect();
        }
        if (!target.isConnected || !rect.width || !rect.height || rect.right <= 0 || rect.bottom <= 0 || rect.left >= innerWidth || rect.top >= innerHeight) continue;
        const style = getComputedStyle(target);
        if (style.visibility === "hidden" || Number(style.opacity) === 0) continue;
        const radius = parseFloat(style.borderTopLeftRadius) || 0;
        const rx = style.borderTopLeftRadius.endsWith("%") ? Math.min(rect.width, rect.height) * radius / 100 : radius;
        const x = Math.max(0, rect.left);
        const y = Math.max(0, rect.top);
        next.push({ x, y, width: Math.min(innerWidth, rect.right) - x, height: Math.min(innerHeight, rect.bottom) - y, rx, clipPath: style.clipPath });
      }
      setHoles((previous) => previous.length === next.length && previous.every((hole, index) => {
        const other = next[index]!;
        return hole.x === other.x && hole.y === other.y && hole.width === other.width && hole.height === other.height && hole.rx === other.rx && hole.clipPath === other.clipPath;
      }) ? previous : next);
    };
    const tick = () => {
      frame = 0;
      measure();
      if (performance.now() < followUntil) frame = requestAnimationFrame(tick);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(tick); };
    const followHover = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest(selector)) {
        followUntil = performance.now() + 240;
        schedule();
      }
    };
    measure();
    const resize = new ResizeObserver(schedule);
    for (const target of targets) resize.observe(target);
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("pointerover", followHover);
    window.addEventListener("pointerout", followHover);
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("pointerover", followHover);
      window.removeEventListener("pointerout", followHover);
    };
  }, [enabled, selector, targetsKey]);

  const active = enabled && Boolean(activeKind && selector && holes.length);
  // Keep the mask mounted so dimming can fade both in and out without a flash.
  return createPortal(
    <div className={`action-guide${active ? " is-active" : ""}`} data-guide-kind={active ? activeKind : undefined} aria-hidden="true">
      <svg className="action-guide-mask" width="100%" height="100%">
        <defs>
          <mask id={mask} x="0" y="0" width="100%" height="100%" maskUnits="userSpaceOnUse">
            <rect width="100%" height="100%" fill="white" />
            {holes.map(({ clipPath, ...hole }, index) => <rect key={index} {...hole} fill="black" style={clipPath === "none" ? undefined : { clipPath: `${clipPath} fill-box` }} />)}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(2, 4, 8, 0.64)" mask={`url(#${mask})`} />
      </svg>
    </div>,
    document.body,
  );
}
